import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import browser from "web/script/utils/browser";
import userProfiler from "web/script/modules/userprofiler";
import {
    BranchBannerCampaign,
    BannerSize,
    getBannerCampaign,
} from "web/script/utils/app-banner-campaign";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import store from "web/redux/store";
import {
    bannerHidden,
    bannerVisible,
    setBannerSize,
    setBannerType,
} from "web/redux/ducks/top-banner";
import { CampaignRawBranchLink } from "web/script/utils/branch-io";
import withReduxProvider from "web/react/redux-provider";
import { useTopBannerClass } from "web/react/hooks/use-top-banner-class/use-top-banner-class";
import SmallAppDownloadBanner from "./small-app-download-banner";
import EmailSignupBanner from "./email-signup-banner";

const BRANCH_LINKS = {
    feed: CampaignRawBranchLink.Feed,
    homepage: CampaignRawBranchLink.Homepage,
    is_pdp: CampaignRawBranchLink.ISPDP,
    oos_pdp: CampaignRawBranchLink.OOSPDP,
    lead_return: CampaignRawBranchLink.LeadReturn,
};

function getBranchBannerCampaign(): BranchBannerCampaign | undefined {
    const branchBannerCampaign = getBannerCampaign(
        analytics.getInitialPageType(),
        analytics.getInitialSubPageType()
    ) as BranchBannerCampaign;

    if (!branchBannerCampaign) {
        return;
    } else {
        branchBannerCampaign.branchLink = BRANCH_LINKS[branchBannerCampaign.flag];
        return branchBannerCampaign;
    }
}

interface TopBannerReduxStateProps {
    fixTopBanner: boolean;
}

function mapStateToProps(state): TopBannerReduxStateProps {
    const { show: fixTopBanner } = state.followDesignerBannerReducer;

    return { fixTopBanner };
}

export function TopBanner({ fixTopBanner }: TopBannerReduxStateProps): null | JSX.Element {
    const [isHidden, setIsHidden] = useState(true);
    const [bannerCampaign, setBannerCampaign] = useState<BranchBannerCampaign>();
    const showEmailBanner =
        browser.android &&
        !userProfiler.isLoggedIn() &&
        environment.getFeature("mg_android_email_signup_banner");
    const analyticsEventCategory = showEmailBanner ? "email_banner" : "app_banner";

    function hideBanner(): void {
        setIsHidden(true);
    }

    function showBanner(): void {
        setIsHidden(false);
    }

    useTopBannerClass(() => document.body, "has-top-banner");

    useEffect(() => {
        if (!isHidden && analyticsEventCategory == "email_banner") {
            store.dispatch(setBannerType(analyticsEventCategory));
            store.dispatch(bannerVisible());
        } else if (!isHidden && bannerCampaign) {
            store.dispatch(bannerVisible());
            store.dispatch(setBannerSize(bannerCampaign.bannerSize));

            if (!userProfiler.hasAppBannerShown()) {
                userProfiler.saveAppBannerShown();
                analytics.event(analyticsEventCategory, "shown", bannerCampaign.name, true);
            }
        } else {
            store.dispatch(bannerHidden());
        }
    }, [isHidden, bannerCampaign, analyticsEventCategory]);

    useEffect(() => {
        const bannerCampaignData = getBranchBannerCampaign();
        if (
            browser.ios &&
            bannerCampaignData &&
            !userProfiler.hasAppBannerBeenDismissed() &&
            environment.get("domainDefaultLanguageIsEnglish")
        ) {
            setBannerCampaign(bannerCampaignData);
            showBanner();
        } else if (showEmailBanner && !userProfiler.hasAppBannerBeenDismissed()) {
            showBanner();
        } else {
            hideBanner();
        }
    }, [showEmailBanner]);

    if (
        environment.get("isPlaUtmCampaign") &&
        environment.get("pageType") == "product" &&
        environment.get("pageSubType") == "in_stock"
    ) {
        return null;
    }

    function onClose(): void {
        analytics.event(
            analyticsEventCategory,
            "dismissed",
            bannerCampaign ? bannerCampaign.name : ""
        );
        hideBanner();

        userProfiler.saveAppBannerDismissed();
    }

    if (showEmailBanner && !isHidden) {
        return <EmailSignupBanner onClose={onClose} />;
    }

    if (isHidden || !bannerCampaign || bannerCampaign.bannerSize !== BannerSize.Small) {
        return null;
    }

    return (
        <SmallAppDownloadBanner
            bannerCampaign={bannerCampaign}
            onClose={onClose}
            fixed={fixTopBanner}
        />
    );
}

export default withReduxProvider(connect(mapStateToProps)(TopBanner));
